import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  resampleAudio,
  float32ToInt16,
  calculateRMS,
  int16ToHex,
  TARGET_SAMPLE_RATE,
  SILENCE_THRESHOLD,
  SILENCE_CHUNKS_BEFORE_PAUSE,
} from '../lib/audioResampler'

// --- Types ---

export type WidgetStep =
  | 'identify'
  | 'ready'
  | 'recording'
  | 'typing'
  | 'transcribing'
  | 'review'
  | 'submitting'
  | 'success'
  | 'error'

export interface PreviewResult {
  summary: string
  type: string
  sentiment: string
  urgency: string
  actionItems: string[]
  confidence: number
}

interface Screenshot {
  blob: Blob
  dataUrl: string
  timestamp: number
}

interface Metadata {
  pageUrl: string
  pageTitle: string
  browserInfo: string
  deviceType: string
  screenResolution: string
  sessionDuration: number
  navigationPath: string[]
}

export interface FeedbackWidgetConfig {
  email?: string
  userName?: string
  productId?: string
  transcriptionWsUrl?: string
}

// Default transcription WebSocket URL
const DEFAULT_TRANSCRIPTION_WS_URL = 'wss://looproom.app/transcription'

export const useFeedbackStore = defineStore('feedback', () => {
  // --- Widget state ---
  const isOpen = ref(false)
  const step = ref<WidgetStep>('identify')
  const error = ref<string | null>(null)

  // --- Form fields ---
  const transcript = ref('')
  const userEmail = ref('')
  const userName = ref('')
  const selectedProductId = ref<number | null>(null)
  const inputMode = ref<'voice' | 'text'>('voice')

  // --- Live transcription ---
  const liveTranscript = ref('')
  const aiPreview = ref<PreviewResult | null>(null)
  const isTranscribing = ref(false)
  const transcriptionWsUrl = ref(DEFAULT_TRANSCRIPTION_WS_URL)

  // --- Audio recording state ---
  const audioBlob = ref<Blob | null>(null)
  const audioPreviewUrl = ref<string | null>(null)
  const isRecording = ref(false)
  const recordingDuration = ref(0)
  const currentAudioLevel = ref(0)
  let mediaRecorder: MediaRecorder | null = null
  let audioChunks: Blob[] = []
  let recordingTimer: ReturnType<typeof setInterval> | null = null
  const MAX_RECORDING_SECONDS = 180

  // --- WebSocket transcription internals ---
  let transcriptionWs: WebSocket | null = null
  let audioContext: AudioContext | null = null
  let mediaStream: MediaStream | null = null
  let scriptProcessor: ScriptProcessorNode | null = null
  let consecutiveSilentChunks = 0
  let isSendingAudio = true

  // --- Screenshot state ---
  const screenshots = ref<Screenshot[]>([])
  const MAX_SCREENSHOTS = 5

  // --- Metadata ---
  const metadata = ref<Metadata>({
    pageUrl: '',
    pageTitle: '',
    browserInfo: '',
    deviceType: '',
    screenResolution: '',
    sessionDuration: 0,
    navigationPath: [],
  })
  const sessionStartTime = ref(Date.now())

  // --- Computed ---

  const canSubmit = computed(
    () =>
      (transcript.value.trim().length > 0 || audioBlob.value !== null) &&
      userEmail.value.trim().length > 0 &&
      selectedProductId.value !== null
  )

  const canProceedFromIdentify = computed(
    () =>
      userEmail.value.trim().length > 0 && selectedProductId.value !== null
  )

  // --- Host app integration ---

  function initFromHost(config: FeedbackWidgetConfig) {
    if (config.email) userEmail.value = config.email
    if (config.userName) userName.value = config.userName
    if (config.productId) selectedProductId.value = config.productId
    if (config.transcriptionWsUrl)
      transcriptionWsUrl.value = config.transcriptionWsUrl
  }

  // --- Widget lifecycle ---

  function open() {
    captureMetadata()
    isOpen.value = true
    error.value = null
    aiPreview.value = null

    // Skip identify step if already have email and product
    if (userEmail.value.trim() && selectedProductId.value) {
      step.value = 'ready'
    } else {
      step.value = 'identify'
    }
  }

  function close() {
    if (isRecording.value) {
      stopVoiceRecording()
    }
    isOpen.value = false
  }

  function reset() {
    transcript.value = ''
    liveTranscript.value = ''
    error.value = null
    step.value = 'identify'
    aiPreview.value = null
    inputMode.value = 'voice'
    currentAudioLevel.value = 0
    deleteRecording()
    screenshots.value = []

    // Re-check if we can skip identify
    if (userEmail.value.trim() && selectedProductId.value) {
      step.value = 'ready'
    }
  }

  function proceedFromIdentify() {
    if (canProceedFromIdentify.value) {
      step.value = 'ready'
    }
  }

  // --- Mode switching ---

  function switchToTyping() {
    inputMode.value = 'text'
    step.value = 'typing'
  }

  function switchToVoice() {
    inputMode.value = 'voice'
    step.value = 'ready'
  }

  // --- Voice recording + WebSocket transcription ---

  async function startVoiceRecording() {
    error.value = null
    liveTranscript.value = ''
    inputMode.value = 'voice'
    step.value = 'recording'

    try {
      // Get microphone access
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: TARGET_SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })

      // Start MediaRecorder for audio blob capture
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4'

      mediaRecorder = new MediaRecorder(mediaStream, { mimeType })
      audioChunks = []

      mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) audioChunks.push(e.data)
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunks, { type: mimeType })
        audioBlob.value = blob
        audioPreviewUrl.value = URL.createObjectURL(blob)
      }

      mediaRecorder.start(1000)

      // Connect to transcription WebSocket
      connectTranscriptionWs()

      // Set up Web Audio API for PCM streaming
      audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE })
      const source = audioContext.createMediaStreamSource(mediaStream)
      scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1)
      consecutiveSilentChunks = 0
      isSendingAudio = true

      scriptProcessor.onaudioprocess = (event: AudioProcessingEvent) => {
        const inputData = event.inputBuffer.getChannelData(0)
        const rms = calculateRMS(inputData)
        currentAudioLevel.value = Math.min(1, rms * 5)

        // Silence detection
        const isSilent = rms < SILENCE_THRESHOLD
        if (isSilent) {
          consecutiveSilentChunks++
          if (consecutiveSilentChunks >= SILENCE_CHUNKS_BEFORE_PAUSE) {
            isSendingAudio = false
          }
        } else {
          consecutiveSilentChunks = 0
          isSendingAudio = true
        }

        // Send audio to transcription server
        if (
          transcriptionWs?.readyState === WebSocket.OPEN &&
          isSendingAudio
        ) {
          const resampledData = resampleAudio(
            inputData,
            audioContext!.sampleRate,
            TARGET_SAMPLE_RATE
          )
          const int16Data = float32ToInt16(resampledData)
          const hexString = int16ToHex(int16Data)

          transcriptionWs.send(
            JSON.stringify({
              type: 'audio',
              speaker_id: userEmail.value || 'anonymous',
              audio: hexString,
            })
          )
        }
      }

      source.connect(scriptProcessor)
      scriptProcessor.connect(audioContext.destination)

      // Start duration timer
      isRecording.value = true
      recordingDuration.value = 0
      recordingTimer = setInterval(() => {
        recordingDuration.value++
        if (recordingDuration.value >= MAX_RECORDING_SECONDS) {
          stopVoiceRecording()
        }
      }, 1000)
    } catch (_err) {
      error.value =
        'Microphone access denied. Please allow microphone access to record audio.'
      step.value = 'ready'
    }
  }

  function connectTranscriptionWs() {
    if (!transcriptionWsUrl.value) return

    transcriptionWs = new WebSocket(transcriptionWsUrl.value)

    transcriptionWs.onopen = () => {
      transcriptionWs?.send(
        JSON.stringify({
          type: 'init',
          session_id: `feedback-${Date.now()}`,
          speaker_id: userEmail.value || 'anonymous',
          speaker_name: userName.value || 'Feedback User',
        })
      )
    }

    transcriptionWs.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string)
        if (data.type === 'transcription' && data.data?.text) {
          liveTranscript.value += data.data.text + ' '
        }
      } catch {
        // Ignore parse errors
      }
    }

    transcriptionWs.onerror = () => {
      console.warn('[Feedback] Transcription WebSocket error')
    }
  }

  function stopVoiceRecording() {
    // Stop MediaRecorder
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop()
    }

    // Stop audio processing
    if (scriptProcessor) {
      scriptProcessor.disconnect()
      scriptProcessor = null
    }
    if (audioContext) {
      audioContext.close()
      audioContext = null
    }

    // Stop media stream tracks
    if (mediaStream) {
      mediaStream.getTracks().forEach((t) => t.stop())
      mediaStream = null
    }

    // Close WebSocket
    if (transcriptionWs) {
      if (transcriptionWs.readyState === WebSocket.OPEN) {
        transcriptionWs.close()
      }
      transcriptionWs = null
    }

    // Stop timer
    if (recordingTimer) {
      clearInterval(recordingTimer)
      recordingTimer = null
    }
    isRecording.value = false
    currentAudioLevel.value = 0

    // Use live transcript as the transcript
    const finalTranscript = liveTranscript.value.trim()
    if (finalTranscript) {
      transcript.value = finalTranscript
    }

    // Move to transcribing step (AI preview)
    step.value = 'transcribing'
  }

  function deleteRecording() {
    if (audioPreviewUrl.value) {
      URL.revokeObjectURL(audioPreviewUrl.value)
    }
    audioBlob.value = null
    audioPreviewUrl.value = null
    recordingDuration.value = 0
    isRecording.value = false
    currentAudioLevel.value = 0
    if (recordingTimer) clearInterval(recordingTimer)
  }

  // --- AI Preview ---

  async function requestPreview(
    previewAction: (args: { transcript: string }) => Promise<PreviewResult>
  ) {
    if (!transcript.value.trim()) {
      step.value = 'review'
      return
    }

    isTranscribing.value = true
    try {
      const result = await previewAction({ transcript: transcript.value })
      aiPreview.value = result
      step.value = 'review'
    } catch (err: unknown) {
      console.error('[Feedback] Preview analysis failed:', err)
      // Still go to review, just without AI preview
      step.value = 'review'
    } finally {
      isTranscribing.value = false
    }
  }

  function addClarification(text: string) {
    if (text.trim()) {
      transcript.value =
        transcript.value.trim() + '\n\n[Clarification]: ' + text.trim()
    }
  }

  // --- Screenshot capture ---

  async function captureScreenshot() {
    if (screenshots.value.length >= MAX_SCREENSHOTS) return

    try {
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: 0.5,
        logging: false,
        ignoreElements: (el: Element) =>
          el.closest('.feedback-widget-panel') !== null,
      })

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b: Blob | null) => {
            if (b) {
              resolve(b)
            } else {
              canvas.toBlob(
                (pngBlob: Blob | null) => {
                  if (pngBlob) resolve(pngBlob)
                  else reject(new Error('Failed to create screenshot blob'))
                },
                'image/png'
              )
            }
          },
          'image/webp',
          0.8
        )
      })

      const dataUrl =
        canvas.toDataURL('image/webp', 0.8) || canvas.toDataURL('image/png')

      screenshots.value.push({
        blob,
        dataUrl,
        timestamp: Date.now(),
      })
    } catch (_err) {
      error.value = 'Screenshot capture failed.'
    }
  }

  function removeScreenshot(index: number) {
    screenshots.value.splice(index, 1)
  }

  // --- Metadata ---

  function captureMetadata() {
    const ua = navigator.userAgent
    let deviceType = 'desktop'
    if (/Mobi|Android/i.test(ua)) deviceType = 'mobile'
    else if (/Tablet|iPad/i.test(ua)) deviceType = 'tablet'

    let browser = 'Unknown'
    if (ua.includes('Firefox/')) browser = 'Firefox'
    else if (ua.includes('Edg/')) browser = 'Edge'
    else if (ua.includes('Chrome/')) browser = 'Chrome'
    else if (ua.includes('Safari/')) browser = 'Safari'

    const os = /Windows/.test(ua)
      ? 'Windows'
      : /Mac/.test(ua)
        ? 'macOS'
        : /Linux/.test(ua)
          ? 'Linux'
          : /Android/.test(ua)
            ? 'Android'
            : /iPhone|iPad/.test(ua)
              ? 'iOS'
              : 'Unknown'

    metadata.value = {
      pageUrl: window.location.href,
      pageTitle: document.title,
      browserInfo: `${browser} on ${os}`,
      deviceType,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      sessionDuration: Math.round(
        (Date.now() - sessionStartTime.value) / 1000
      ),
      navigationPath: getNavigationPath(),
    }
  }

  function getNavigationPath(): string[] {
    return [window.location.pathname]
  }

  // --- Proceed from typing to review ---

  function proceedFromTyping() {
    if (transcript.value.trim()) {
      step.value = 'transcribing'
    }
  }

  return {
    // State
    isOpen,
    step,
    error,
    transcript,
    userEmail,
    userName,
    selectedProductId,
    inputMode,
    metadata,
    canSubmit,
    canProceedFromIdentify,

    // Live transcription
    liveTranscript,
    aiPreview,
    isTranscribing,
    currentAudioLevel,

    // Audio
    audioBlob,
    audioPreviewUrl,
    isRecording,
    recordingDuration,
    MAX_RECORDING_SECONDS,

    // Screenshots
    screenshots,
    MAX_SCREENSHOTS,

    // Methods
    initFromHost,
    open,
    close,
    reset,
    proceedFromIdentify,
    switchToTyping,
    switchToVoice,
    startVoiceRecording,
    stopVoiceRecording,
    deleteRecording,
    requestPreview,
    addClarification,
    proceedFromTyping,
    captureMetadata,
    captureScreenshot,
    removeScreenshot,
  }
})
